import { Game, Factor, Tag, FactorDirection } from "../types";
import { getStadium, getWindEffect, parkFactorScore } from "./stadiums";

// ── WEIGHTS ──────────────────────────────────────────────────────────────────
// Must sum to 1.0
const WEIGHTS = {
  park: 0.25,
  pitcher: 0.3, // strongest single predictor
  slg: 0.15,
  total: 0.2,
  weather: 0.1,
};

// ── BASELINE ─────────────────────────────────────────────────────────────────
// 2024 season: ~12% of games had zero HRs
// Model output is calibrated around this baseline
const BASELINE = 0.12;

// ── HELPERS ──────────────────────────────────────────────────────────────────

function dir(score: number): FactorDirection {
  if (score >= 60) return "good";
  if (score >= 35) return "neutral";
  return "bad";
}

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

// ── FACTOR SCORERS ───────────────────────────────────────────────────────────

/**
 * Park factor score (0–100)
 * PNC 0.78 → ~90   Coors 1.44 → ~8
 */
function scorePark(homeAbbr: string): { score: number; factor: Factor } {
  const stadium = getStadium(homeAbbr);
  const score = parkFactorScore(homeAbbr);
  const pf = stadium?.parkFactor ?? 1.0;

  let sub = "League average park";
  if (pf <= 0.85) sub = `${stadium?.name} · strong HR suppressor`;
  else if (pf <= 0.95) sub = `${stadium?.name} · mild suppressor`;
  else if (pf >= 1.2) sub = `${stadium?.name} · extreme HR park`;
  else if (pf >= 1.1) sub = `${stadium?.name} · HR-friendly`;
  else sub = `${stadium?.name} · neutral`;

  return {
    score,
    factor: {
      label: "Park Factor",
      value: pf >= 1 ? `+${pf.toFixed(2)}` : `−${(1 - pf + 1).toFixed(2)}`,
      sub,
      score,
      dir: dir(score),
    },
  };
}

/**
 * Pitcher HR/9 score (0–100)
 * Average of both starters. Lower HR/9 = higher score.
 * Elite: < 0.60  Good: 0.60–0.90  Avg: 0.90–1.20  Bad: > 1.20
 */
function scorePitcher(
  awayHR9: number,
  homeHR9: number,
  awayPitcher: string,
  homePitcher: string
): { score: number; factor: Factor } {
  const avg = (awayHR9 + homeHR9) / 2;
  // Map 0.40 → 100, 1.60 → 0
  const raw = ((1.6 - avg) / (1.6 - 0.4)) * 100;
  const score = clamp(Math.round(raw), 0, 100);

  const awayLast = awayPitcher.split(" ").pop() ?? awayPitcher;
  const homeLast = homePitcher.split(" ").pop() ?? homePitcher;

  let sub = `${awayLast} ${awayHR9} · ${homeLast} ${homeHR9}`;
  let value = avg.toFixed(2);

  return {
    score,
    factor: {
      label: "SP HR/9 Avg",
      value,
      sub,
      score,
      dir: dir(score),
    },
  };
}

/**
 * Team SLG score (0–100)
 * Average of both teams. Lower SLG = higher score.
 * Good: < .380  Neutral: .380–.430  Bad: > .430
 */
function scoreSLG(
  awaySLG: number,
  homeSLG: number,
  awayAbbr: string,
  homeAbbr: string
): { score: number; factor: Factor } {
  const avg = (awaySLG + homeSLG) / 2;
  // Map .320 → 100, .500 → 0
  const raw = ((0.5 - avg) / (0.5 - 0.32)) * 100;
  const score = clamp(Math.round(raw), 0, 100);

  return {
    score,
    factor: {
      label: "Team SLG Avg",
      value: `.${Math.round(avg * 1000)}`,
      sub: `${awayAbbr} .${Math.round(
        awaySLG * 1000
      )} · ${homeAbbr} .${Math.round(homeSLG * 1000)}`,
      score,
      dir: dir(score),
    },
  };
}

/**
 * Game total score (0–100)
 * Lower O/U = fewer expected runs = lower HR probability
 * Good: < 7.5   Neutral: 7.5–9.0   Bad: > 9.0
 */
function scoreTotal(total: number): { score: number; factor: Factor } {
  // Map 6.0 → 100, 11.0 → 0
  const raw = ((11.0 - total) / (11.0 - 6.0)) * 100;
  const score = clamp(Math.round(raw), 0, 100);

  let sub = "League average total";
  if (total <= 7.0) sub = "Low total · very favorable";
  else if (total <= 7.5) sub = "Below avg · favorable";
  else if (total <= 8.5) sub = "Average total";
  else if (total <= 9.5) sub = "Above avg · unfavorable";
  else sub = "High total · avoid";

  return {
    score,
    factor: {
      label: "Game Total",
      value: total.toFixed(1),
      sub,
      score,
      dir: dir(score),
    },
  };
}

/**
 * Weather score (0–100)
 * Dome = 65 (neutral positive — controlled environment)
 * Cold + wind in = highest scores
 * Hot + wind out = lowest scores
 */
function scoreWeather(
  homeAbbr: string,
  tempF: number,
  windMph: number,
  windDeg: number
): { score: number; factor: Factor } {
  const stadium = getStadium(homeAbbr);

  if (!stadium) {
    return {
      score: 50,
      factor: {
        label: "Weather",
        value: "–",
        sub: "No weather data",
        score: 50,
        dir: "neutral",
      },
    };
  }

  if (stadium.dome) {
    return {
      score: 65,
      factor: {
        label: "Weather",
        value: "Dome",
        sub: `${stadium.name} · controlled environment`,
        score: 65,
        dir: "good",
      },
    };
  }

  const windEffect = getWindEffect(windDeg, stadium);

  // Temperature score: cold is good (denser air)
  // Map 35°F → 100, 90°F → 0
  const tempScore = clamp(Math.round(((90 - tempF) / (90 - 35)) * 100), 0, 100);

  // Wind score
  let windScore = 50;
  if (windEffect === "in") windScore = clamp(50 + windMph * 2, 50, 95);
  if (windEffect === "out") windScore = clamp(50 - windMph * 2, 5, 50);
  if (windEffect === "cross") windScore = 50;

  const score = clamp(Math.round(tempScore * 0.5 + windScore * 0.5), 0, 100);

  const windLabel =
    windEffect === "dome"
      ? "Dome"
      : windEffect === "in"
      ? `${windMph}mph IN ↓`
      : windEffect === "out"
      ? `${windMph}mph OUT ↑`
      : `${windMph}mph crosswind`;

  return {
    score,
    factor: {
      label: "Weather",
      value: `${Math.round(tempF)}°F`,
      sub: windLabel,
      score,
      dir: dir(score),
    },
  };
}

// ── TAGS ─────────────────────────────────────────────────────────────────────

function buildTags(
  parkScore: number,
  pitcherScore: number,
  slgScore: number,
  totalScore: number,
  weatherScore: number,
  total: number,
  tempF: number,
  windEffect: string,
  windMph: number,
  homeAbbr: string,
  awayHR9: number,
  homeHR9: number,
  dome: boolean
): Tag[] {
  const tags: Tag[] = [];
  const stadium = getStadium(homeAbbr);

  if (dome) {
    tags.push({ label: "Dome", type: "good" });
  } else {
    if (windEffect === "in" && windMph >= 8)
      tags.push({ label: `Wind IN ${windMph}mph`, type: "good" });
    if (windEffect === "out" && windMph >= 8)
      tags.push({ label: `Wind OUT ${windMph}mph`, type: "bad" });
    if (tempF < 50)
      tags.push({ label: `${Math.round(tempF)}°F Cold`, type: "good" });
    else if (tempF > 80)
      tags.push({ label: `${Math.round(tempF)}°F Warm`, type: "bad" });
  }

  if (stadium && stadium.parkFactor <= 0.88)
    tags.push({ label: `Park ${stadium.parkFactor.toFixed(2)}`, type: "good" });
  else if (stadium && stadium.parkFactor >= 1.15)
    tags.push({ label: `Park +${stadium.parkFactor.toFixed(2)}`, type: "bad" });

  const avgHR9 = (awayHR9 + homeHR9) / 2;
  if (avgHR9 <= 0.65)
    tags.push({ label: `SP HR/9 ${avgHR9.toFixed(2)}`, type: "good" });
  else if (avgHR9 >= 1.1)
    tags.push({ label: `SP HR/9 ${avgHR9.toFixed(2)}`, type: "bad" });

  if (total <= 7.0) tags.push({ label: `O/U ${total}`, type: "good" });
  else if (total >= 9.5) tags.push({ label: `O/U ${total}`, type: "bad" });
  else tags.push({ label: `O/U ${total}`, type: "neutral" });

  return tags.slice(0, 4);
}

// ── MAIN SCORER ──────────────────────────────────────────────────────────────

export interface ScoreInput {
  id: number;
  away: string;
  home: string;
  awayAbbr: string;
  homeAbbr: string;
  venue: string;
  time: string;
  awayPitcher: string;
  homePitcher: string;
  awayHR9: number;
  homeHR9: number;
  awaySLG: number;
  homeSLG: number;
  total: number;
  tempF: number;
  windMph: number;
  windDeg: number;
}

export function scoreGame(input: ScoreInput): Game {
  const park = scorePark(input.homeAbbr);
  const pitcher = scorePitcher(
    input.awayHR9,
    input.homeHR9,
    input.awayPitcher,
    input.homePitcher
  );
  const slg = scoreSLG(
    input.awaySLG,
    input.homeSLG,
    input.awayAbbr,
    input.homeAbbr
  );
  const total = scoreTotal(input.total);
  const weather = scoreWeather(
    input.homeAbbr,
    input.tempF,
    input.windMph,
    input.windDeg
  );

  // Weighted composite 0–100
  const composite =
    park.score * WEIGHTS.park +
    pitcher.score * WEIGHTS.pitcher +
    slg.score * WEIGHTS.slg +
    total.score * WEIGHTS.total +
    weather.score * WEIGHTS.weather;

  // Convert to probability calibrated around 12% baseline
  // composite 50 = baseline (12%), 100 = ~35%, 0 = ~2%
  const prob = clamp(Math.round(BASELINE * 100 * (composite / 50)), 2, 42);

  const stadium = getStadium(input.homeAbbr);
  const windEffect = stadium ? getWindEffect(input.windDeg, stadium) : "cross";

  const tags = buildTags(
    park.score,
    pitcher.score,
    slg.score,
    total.score,
    weather.score,
    input.total,
    input.tempF,
    windEffect,
    input.windMph,
    input.homeAbbr,
    input.awayHR9,
    input.homeHR9,
    stadium?.dome ?? false
  );

  return {
    id: input.id,
    away: input.away,
    home: input.home,
    awayAbbr: input.awayAbbr,
    homeAbbr: input.homeAbbr,
    venue: input.venue,
    time: input.time,
    prob,
    strong: prob >= 25,
    awayPitcher: input.awayPitcher,
    homePitcher: input.homePitcher,
    awayHR9: input.awayHR9,
    homeHR9: input.homeHR9,
    factors: {
      park: park.factor,
      pitcher: pitcher.factor,
      slg: slg.factor,
      total: total.factor,
      weather: weather.factor,
    },
    tags,
  };
}
